import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { dashboardAPI, alertsAPI } from '../lib/api';
import { formatCurrency as fmtCurrencyIntl } from '../lib/formatters';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
    TrendingUp, 
    CreditCard, 
    Banknote, 
    Building2, 
    AlertCircle,
    Calendar,
    ArrowUpRight,
    Loader2,
    FileCheck,
    MoreHorizontal,
    Package,
    AlertTriangle,
    Mail,
} from 'lucide-react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
    Legend,
    PieChart,
    Pie,
    Cell
} from 'recharts';

const CHART_COLORS = ['#2A9D8F', '#264653', '#E9C46A', '#8B5CF6', '#E76F51', '#6B7280'];

const Dashboard = () => {
    const { t, i18n } = useTranslation(['dashboard', 'common']);
    const lang = (i18n.resolvedLanguage || 'fr').slice(0, 2);
    const [stats, setStats] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [chartPeriod, setChartPeriod] = useState('week');
    const [pieChartPeriod, setPieChartPeriod] = useState('day');
    const [pieChartData, setPieChartData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alerts, setAlerts] = useState({ low_stock: [], dues: [], low_stock_count: 0, dues_count: 0, dues_total: 0 });
    const [sendingLowStock, setSendingLowStock] = useState(false);
    const [sendingDues, setSendingDues] = useState(false);

    useEffect(() => {
        fetchDashboardData();
        fetchAlerts();
    }, []);

    const fetchAlerts = async () => {
        try {
            const { data } = await alertsAPI.get();
            setAlerts(data);
        } catch (e) {
            // silent
        }
    };

    const handleSendLowStockEmail = async () => {
        setSendingLowStock(true);
        try {
            const { data } = await alertsAPI.sendLowStock();
            if (data.sent) {
                toast.success(`Low stock alert sent to ${data.recipients.length} admin(s)`);
            } else {
                toast.info(data.reason || 'Email not sent');
            }
        } catch (e) {
            toast.error('Failed to send email');
        } finally {
            setSendingLowStock(false);
        }
    };

    const handleSendDueEmail = async () => {
        setSendingDues(true);
        try {
            const { data } = await alertsAPI.sendDueReminder();
            if (data.sent) {
                toast.success(`Due reminder sent to ${data.recipients.length} admin(s)`);
            } else {
                toast.info(data.reason || 'Email not sent');
            }
        } catch (e) {
            toast.error('Failed to send email');
        } finally {
            setSendingDues(false);
        }
    };

    useEffect(() => {
        fetchChartData(chartPeriod);
    }, [chartPeriod]);

    useEffect(() => {
        fetchPieChartData(pieChartPeriod);
    }, [pieChartPeriod]);

    const fetchDashboardData = async () => {
        try {
            const response = await dashboardAPI.getStats();
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchChartData = async (period) => {
        try {
            const response = await dashboardAPI.getChartData(period);
            setChartData(response.data);
        } catch (error) {
            console.error('Failed to fetch chart data:', error);
        }
    };

    const fetchPieChartData = async (period) => {
        try {
            const response = await dashboardAPI.getPaymentBreakdown(period);
            const breakdown = response.data.breakdown;
            
            const data = [
                { name: 'Cash', value: breakdown.cash || 0, color: '#2A9D8F' },
                { name: 'Card', value: breakdown.card || 0, color: '#264653' },
                { name: 'Bank Transfer', value: breakdown.bank_transfer || 0, color: '#E9C46A' },
                { name: 'Cheque', value: breakdown.cheque || 0, color: '#8B5CF6' },
                { name: 'Due', value: breakdown.due || 0, color: '#E76F51' },
                { name: 'Others', value: breakdown.others || 0, color: '#6B7280' },
            ].filter(item => item.value > 0);
            
            setPieChartData(data);
        } catch (error) {
            console.error('Failed to fetch pie chart data:', error);
        }
    };

    const formatCurrency = (value) => fmtCurrencyIntl(value, lang);

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const localeTag = lang === 'fr' ? 'fr-FR' : 'en-GB';
        return date.toLocaleDateString(localeTag, { day: '2-digit', month: 'short' });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader2 className="w-8 h-8 animate-spin text-[#C08478]" />
            </div>
        );
    }

    const statCards = [
        {
            title: t('dashboard:kpis.todays_sales'),
            value: formatCurrency(stats?.total_sales_today),
            icon: TrendingUp,
            color: '#2A9D8F',
            bgColor: 'bg-[#2A9D8F]/10',
        },
        {
            title: t('dashboard:kpis.cash_today'),
            value: formatCurrency(stats?.cash_today),
            icon: Banknote,
            color: '#2A9D8F',
            bgColor: 'bg-[#2A9D8F]/10',
        },
        {
            title: t('dashboard:kpis.card_today'),
            value: formatCurrency(stats?.card_today),
            icon: CreditCard,
            color: '#264653',
            bgColor: 'bg-[#264653]/10',
        },
        {
            title: t('dashboard:kpis.bank_transfer'),
            value: formatCurrency(stats?.bank_transfer_today),
            icon: Building2,
            color: '#E9C46A',
            bgColor: 'bg-[#E9C46A]/20',
        },
        {
            title: t('dashboard:kpis.cheque_today'),
            value: formatCurrency(stats?.cheque_today),
            icon: FileCheck,
            color: '#8B5CF6',
            bgColor: 'bg-[#8B5CF6]/10',
        },
        {
            title: t('dashboard:kpis.due_today'),
            value: formatCurrency(stats?.total_due_today),
            icon: AlertCircle,
            color: '#E76F51',
            bgColor: 'bg-[#E76F51]/10',
        },
        {
            title: t('dashboard:kpis.transactions'),
            value: stats?.total_transactions_today || 0,
            icon: Calendar,
            color: '#C08478',
            bgColor: 'bg-[#C08478]/10',
        },
    ];

    const periodStats = [
        { label: t('dashboard:periods.this_week'), sales: stats?.total_sales_week, due: stats?.total_due_week },
        { label: t('dashboard:periods.this_month'), sales: stats?.total_sales_month, due: stats?.total_due_month },
    ];

    const totalPieValue = pieChartData.reduce((sum, item) => sum + item.value, 0);

    return (
        <div data-testid="dashboard-page" className="space-y-8 animate-fadeIn">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight">{t('dashboard:title')}</h1>
                <p className="text-gray-500 mt-1">
                    {lang === 'fr' ? 'Aperçu de vos performances commerciales' : 'Overview of your sales performance'}
                </p>
            </div>

            {/* Stats Grid - Single Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                {statCards.map((stat, index) => (
                    <Card 
                        key={stat.title}
                        className="stats-card border-0 shadow-sm hover:shadow-md"
                        data-testid={`stat-card-${index}`}
                    >
                        <CardContent className="p-3">
                            <div className={`w-8 h-8 rounded-lg ${stat.bgColor} flex items-center justify-center mb-2`}>
                                <stat.icon size={16} style={{ color: stat.color }} />
                            </div>
                            <p className="text-xs text-gray-500 mb-0.5 truncate">{stat.title}</p>
                            <p className="text-base font-bold text-[#1A1A1A] euro-amount">{stat.value}</p>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Alert Widgets - Low Stock + Dues */}
            {(alerts.low_stock_count > 0 || alerts.dues_count > 0) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Low Stock Widget */}
                    {alerts.low_stock_count > 0 && (
                        <Card data-testid="low-stock-widget" className="border-0 shadow-sm border-l-4 border-l-[#E9C46A]">
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <Package className="text-[#E9C46A]" size={18} />
                                        {t('dashboard:sections.low_stock')}
                                        <Badge className="bg-[#E9C46A]/20 text-[#1A1A1A]">{alerts.low_stock_count}</Badge>
                                    </CardTitle>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={handleSendLowStockEmail}
                                        disabled={sendingLowStock}
                                        data-testid="send-low-stock-email"
                                    >
                                        {sendingLowStock ? <Loader2 size={12} className="animate-spin mr-1" /> : <Mail size={12} className="mr-1" />}
                                        Email Admins
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {alerts.low_stock.slice(0, 6).map((p) => (
                                        <Link
                                            key={p.id}
                                            to="/products"
                                            className="flex justify-between items-center text-sm p-2 rounded-md hover:bg-[#F5F5F5]"
                                        >
                                            <div>
                                                <p className="font-medium">{p.name}</p>
                                                <p className="text-xs text-gray-400 font-mono">{p.sku}</p>
                                            </div>
                                            <span className="text-[#E76F51] font-mono font-bold">
                                                {p.stock_qty} / {p.low_stock_threshold} {p.unit}
                                            </span>
                                        </Link>
                                    ))}
                                </div>
                                {alerts.low_stock_count > 6 && (
                                    <Link to="/products" className="text-xs text-[#C08478] mt-2 inline-block">
                                        View all {alerts.low_stock_count} →
                                    </Link>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Dues Widget */}
                    {alerts.dues_count > 0 && (
                        <Card data-testid="dues-widget" className="border-0 shadow-sm border-l-4 border-l-[#E76F51]">
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <AlertTriangle className="text-[#E76F51]" size={18} />
                                        {t('dashboard:sections.dues')}
                                        <Badge className="bg-[#E76F51]/20 text-[#E76F51]">{alerts.dues_count}</Badge>
                                    </CardTitle>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={handleSendDueEmail}
                                        disabled={sendingDues}
                                        data-testid="send-due-email"
                                    >
                                        {sendingDues ? <Loader2 size={12} className="animate-spin mr-1" /> : <Mail size={12} className="mr-1" />}
                                        Email Admins
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {alerts.dues.slice(0, 6).map((d) => (
                                        <Link
                                            key={d.id}
                                            to="/dues"
                                            className="flex justify-between items-center text-sm p-2 rounded-md hover:bg-[#F5F5F5]"
                                        >
                                            <div>
                                                <p className="font-medium">{d.company_name || '-'}</p>
                                                <p className="text-xs text-gray-400 font-mono">{d.invoice_number}</p>
                                            </div>
                                            <span className="text-[#E76F51] font-mono font-bold">
                                                {formatCurrency(d.due_amount)}
                                            </span>
                                        </Link>
                                    ))}
                                </div>
                                <div className="mt-3 pt-3 border-t flex justify-between items-center text-sm">
                                    <span className="text-gray-500">{t('dashboard:sections.total_outstanding')}</span>
                                    <span className="font-mono font-bold text-[#E76F51] text-base">
                                        {formatCurrency(alerts.dues_total)}
                                    </span>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            {/* Period Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {periodStats.map((period) => (
                    <Card key={period.label} className="border-0 shadow-sm">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-semibold text-[#1A1A1A]">{period.label}</h3>
                                <ArrowUpRight size={16} className="text-gray-400" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-gray-500 mb-1">Total Sales</p>
                                    <p className="text-2xl font-bold text-[#2A9D8F] euro-amount">
                                        {formatCurrency(period.sales)}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 mb-1">Total Due</p>
                                    <p className="text-2xl font-bold text-[#E76F51] euro-amount">
                                        {formatCurrency(period.due)}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Area Chart */}
                <Card className="border-0 shadow-sm lg:col-span-2">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg font-semibold">Sales Trend</CardTitle>
                            <Tabs value={chartPeriod} onValueChange={setChartPeriod}>
                                <TabsList className="bg-[#F5F5F5]">
                                    <TabsTrigger 
                                        value="week" 
                                        data-testid="chart-period-week"
                                        className="data-[state=active]:bg-white"
                                    >
                                        Week
                                    </TabsTrigger>
                                    <TabsTrigger 
                                        value="month"
                                        data-testid="chart-period-month"
                                        className="data-[state=active]:bg-white"
                                    >
                                        Month
                                    </TabsTrigger>
                                </TabsList>
                            </Tabs>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="h-72">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData}>
                                    <defs>
                                        <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#2A9D8F" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#2A9D8F" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                                    <XAxis 
                                        dataKey="date" 
                                        tickFormatter={formatDate}
                                        tick={{ fontSize: 12, fill: '#666' }}
                                    />
                                    <YAxis 
                                        tick={{ fontSize: 12, fill: '#666' }}
                                        tickFormatter={(val) => `€${val}`}
                                    />
                                    <Tooltip 
                                        formatter={(value) => [formatCurrency(value), 'Sales']}
                                        labelFormatter={formatDate}
                                        contentStyle={{ borderRadius: '8px', border: '1px solid #E5E5E5' }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="sales"
                                        stroke="#2A9D8F"
                                        strokeWidth={2}
                                        fill="url(#colorSales)"
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Pie Chart - Payment Method Breakdown with Tabs */}
                <Card className="border-0 shadow-sm">
                    <CardHeader className="pb-2">
                        <div className="flex flex-col gap-2">
                            <CardTitle className="text-lg font-semibold">Payment Methods</CardTitle>
                            <Tabs value={pieChartPeriod} onValueChange={setPieChartPeriod}>
                                <TabsList className="bg-[#F5F5F5] w-full">
                                    <TabsTrigger 
                                        value="day" 
                                        data-testid="pie-period-day"
                                        className="data-[state=active]:bg-white flex-1"
                                    >
                                        Daily
                                    </TabsTrigger>
                                    <TabsTrigger 
                                        value="week"
                                        data-testid="pie-period-week"
                                        className="data-[state=active]:bg-white flex-1"
                                    >
                                        Weekly
                                    </TabsTrigger>
                                    <TabsTrigger 
                                        value="month"
                                        data-testid="pie-period-month"
                                        className="data-[state=active]:bg-white flex-1"
                                    >
                                        Monthly
                                    </TabsTrigger>
                                </TabsList>
                            </Tabs>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64">
                            {totalPieValue > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={pieChartData}
                                            cx="50%"
                                            cy="45%"
                                            innerRadius={45}
                                            outerRadius={75}
                                            paddingAngle={2}
                                            dataKey="value"
                                        >
                                            {pieChartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip 
                                            formatter={(value) => formatCurrency(value)}
                                            contentStyle={{ borderRadius: '8px', border: '1px solid #E5E5E5' }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="flex items-center justify-center h-full text-gray-400">
                                    <p>No transactions for this period</p>
                                </div>
                            )}
                        </div>
                        {/* Legend */}
                        <div className="flex flex-wrap justify-center gap-3 mt-2">
                            {pieChartData.map((item) => (
                                <div key={item.name} className="flex items-center gap-1.5">
                                    <div 
                                        className="w-3 h-3 rounded-full" 
                                        style={{ backgroundColor: item.color }}
                                    />
                                    <span className="text-xs text-gray-600">{item.name}</span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Bar Chart */}
            <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-semibold">Sales vs Due</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                                <XAxis 
                                    dataKey="date" 
                                    tickFormatter={formatDate}
                                    tick={{ fontSize: 12, fill: '#666' }}
                                />
                                <YAxis 
                                    tick={{ fontSize: 12, fill: '#666' }}
                                    tickFormatter={(val) => `€${val}`}
                                />
                                <Tooltip 
                                    formatter={(value, name) => [
                                        formatCurrency(value), 
                                        name === 'sales' ? 'Paid' : 'Due'
                                    ]}
                                    labelFormatter={formatDate}
                                    contentStyle={{ borderRadius: '8px', border: '1px solid #E5E5E5' }}
                                />
                                <Legend />
                                <Bar dataKey="sales" name="Paid" fill="#2A9D8F" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="due" name="Due" fill="#E76F51" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default Dashboard;
